"use client";

/**
 * Interactive renovation-mix donut (residence page).
 *
 * The ring PAINTS ITSELF clockwise when it scrolls into view (each
 * segment's dasharray transitions from 0 with a delay proportional to
 * its start angle, so the tiers draw sequentially), and the legend bars
 * fill in the same pass. Hovering (or tapping) a segment or a legend
 * row highlights that tier everywhere: the other segments dim, the
 * hovered one thickens, the centre stat flips to the hovered tier and
 * takes its colour, and the matching legend row lifts on a soft wash.
 *
 * Clicking (or tapping) a segment or legend row deep-links into the
 * listings page filtered to that tier (hrefFor builds the URL), so the
 * donut is a filter, not just a picture.
 *
 * Touch adaptation: hover doesn't exist there, so LONG-PRESS (350ms)
 * takes its place - it highlights the tier (with a vibration tick on
 * devices that support it) without navigating; a quick tap navigates.
 * Moving the finger (scrolling) cancels the press.
 *
 * SSR/no-JS keeps the ring fully drawn only under reduced motion; for
 * everyone else the draw starts once the IntersectionObserver fires,
 * which is also why this stays a leaf client component.
 */
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale } from "@/lib/locale-context";
import { haptic } from "@/lib/haptics";

export type RenoDonutItem = {
  tier: string;
  color: string;
  /** Full tier name - legend rows. */
  label: string;
  /** Short tier name - the only one that fits inside the ring. */
  shortLabel: string;
  n: number;
  /** Exact share, 0-100. */
  pct: number;
  /** Display share ("63%", "<1%"). */
  pctLabel: string;
};

const DRAW_MS = 900;

export default function RenovationDonut({
  items,
  note,
  hrefFor,
  hint,
}: {
  items: RenoDonutItem[];
  note: string;
  /** Deep-link target per tier (unlocalized path); when set, clicking a
   *  segment or legend row navigates there. */
  hrefFor?: (tier: string) => string;
  /** One-line affordance hint shown under the note when hrefFor is set. */
  hint?: string;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [drawn, setDrawn] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const router = useRouter();
  const { locale } = useLocale();
  const go = (tier: string) => {
    if (!hrefFor) return;
    haptic(8);
    router.push(`/${locale}${hrefFor(tier)}`);
  };

  // Long-press = the touch stand-in for hover: highlight the tier
  // without navigating. A quick tap still navigates (the click that
  // follows a long-press is swallowed via longPressed).
  const pressTimer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const pressStart = (tier: string) => {
    longPressed.current = false;
    pressTimer.current = window.setTimeout(() => {
      longPressed.current = true;
      setHover((cur) => (cur === tier ? null : tier));
      haptic(12);
    }, 350);
  };
  const pressCancel = () => {
    if (pressTimer.current != null) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  const activate = (tier: string) => {
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    go(tier);
  };

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDrawn(true);
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setDrawn(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const dominant = items.reduce((best, cur) => (cur.n > best.n ? cur : best), items[0]);
  const active = (hover != null && items.find((i) => i.tier === hover)) || dominant;

  // Segment geometry: ~1.2 units shaved per segment reads as a gap
  // between the rounded caps; tiny tiers keep a 0.5 sliver so every
  // tier in the legend is findable on the ring.
  let cum = 0;
  const segs = items.map((item) => {
    const seg = Math.max(item.pct - 1.2, 0.5);
    const out = { ...item, seg, offset: cum };
    cum += item.pct;
    return out;
  });

  return (
    <div
      ref={rootRef}
      className="grid grid-cols-1 items-center gap-6 rounded-2xl border border-gray-200 bg-white p-5 sm:grid-cols-[12rem_1fr] sm:gap-10 sm:p-6"
    >
      <div className="relative mx-auto h-40 w-40 shrink-0">
        <svg viewBox="0 0 42 42" className="h-full w-full -rotate-90 overflow-visible">
          <circle cx="21" cy="21" r="15.9155" fill="none" stroke="#f3f4f6" strokeWidth="5.5" />
          {segs.map((s) => {
            const dash = drawn ? s.seg : 0.0001;
            const dimmed = hover != null && hover !== s.tier;
            return (
              <circle
                key={s.tier}
                cx="21" cy="21" r="15.9155" fill="none"
                stroke={s.color}
                strokeWidth={hover === s.tier ? 6.6 : 5.5}
                strokeDasharray={`${dash} ${100 - dash}`}
                strokeDashoffset={-s.offset}
                strokeLinecap="round"
                className="cursor-pointer"
                style={{
                  opacity: !drawn ? 0 : dimmed ? 0.3 : 1,
                  pointerEvents: "stroke",
                  transition: [
                    // Per-segment delay ~ start angle → sequential clockwise paint.
                    `stroke-dasharray ${DRAW_MS}ms cubic-bezier(.22,1,.36,1) ${Math.round((s.offset / 100) * 500)}ms`,
                    "opacity 250ms ease",
                    "stroke-width 200ms ease",
                  ].join(", "),
                }}
                onMouseEnter={() => setHover(s.tier)}
                onMouseLeave={() => setHover(null)}
                onTouchStart={() => pressStart(s.tier)}
                onTouchEnd={pressCancel}
                onTouchMove={pressCancel}
                onTouchCancel={pressCancel}
                onClick={() => activate(s.tier)}
              >
                <title>{s.label}</title>
              </circle>
            );
          })}
        </svg>
        <div className="pointer-events-none absolute inset-6 flex flex-col items-center justify-center text-center">
          <span
            className="text-2xl font-extrabold tabular-nums leading-none transition-colors duration-200"
            style={{ color: hover != null ? active.color : undefined }}
          >
            {active.pctLabel}
          </span>
          <span className="mt-1 w-full truncate text-[11px] font-semibold text-gray-500">
            {active.shortLabel}
          </span>
        </div>
      </div>
      <div className="min-w-0 w-full">
        <ul className="-mx-2 space-y-0.5">
          {segs.map((s, i) => {
            const dimmed = hover != null && hover !== s.tier;
            return (
              <li
                key={s.tier}
                className={`rounded-lg px-2 py-1.5 text-sm transition-[background-color,opacity] duration-200 ${
                  hover === s.tier ? "bg-gray-50 dark:bg-white/5" : ""
                } ${hrefFor ? "cursor-pointer select-none" : ""}`}
                style={{ opacity: dimmed ? 0.45 : 1 }}
                onMouseEnter={() => setHover(s.tier)}
                onMouseLeave={() => setHover(null)}
                onTouchStart={() => pressStart(s.tier)}
                onTouchEnd={pressCancel}
                onTouchMove={pressCancel}
                onTouchCancel={pressCancel}
                onClick={() => activate(s.tier)}
              >
                <div className="flex items-center gap-2.5">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="min-w-0 flex-1 truncate text-gray-700">{s.label}</span>
                  <span className="font-bold tabular-nums text-gray-900">{s.pctLabel}</span>
                  <span className="w-8 text-right text-xs tabular-nums text-gray-400">{s.n}</span>
                </div>
                <div className="mt-1.5 ml-5 h-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: drawn ? `${Math.max(s.pct, 2)}%` : 0,
                      backgroundColor: s.color,
                      transition: `width 700ms cubic-bezier(.22,1,.36,1) ${200 + i * 80}ms`,
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
        <p className="mt-4 text-[11px] text-gray-400 leading-relaxed">
          {note}
          {hrefFor && hint ? (
            <span className="ml-1 font-medium text-gray-600">{hint}</span>
          ) : null}
        </p>
      </div>
    </div>
  );
}
